import type { Session, User } from "@supabase/supabase-js";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { authConfigured, getAuthClient } from "../lib/appcradle-auth";

interface AuthValue {
  configured: boolean;
  loading: boolean;
  session: Session | null;
  user: User | null;
  requestEmailCode: (email: string) => Promise<void>;
  verifyEmailCode: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

function clearCredentialFragment(): void {
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  if (["access_token", "refresh_token", "error", "error_description"].some((key) => fragment.has(key))) {
    window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    void (async () => {
      const client = await getAuthClient();
      if (!active || !client) return;
      const { data: listener } = client.auth.onAuthStateChange((_event, nextSession) => {
        if (!active) return;
        setSession(nextSession);
        if (nextSession) clearCredentialFragment();
      });
      unsubscribe = () => listener.subscription.unsubscribe();
      const { data } = await client.auth.getSession();
      if (!active) return;
      setSession(data.session);
      clearCredentialFragment();
    })().catch(() => undefined).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  const value = useMemo<AuthValue>(() => ({
    configured: authConfigured,
    loading,
    session,
    user: session?.user ?? null,
    async requestEmailCode(email) {
      const client = await getAuthClient();
      if (!client) throw new Error("AppCradle Auth is not configured for this build.");
      const { error } = await client.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: { shouldCreateUser: true }
      });
      if (error) throw error;
    },
    async verifyEmailCode(email, code) {
      const client = await getAuthClient();
      if (!client) throw new Error("AppCradle Auth is not configured for this build.");
      const { error } = await client.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: code.trim(),
        type: "email"
      });
      if (error) throw error;
    },
    async signOut() {
      const client = await getAuthClient();
      if (!client) return;
      const { error } = await client.auth.signOut({ scope: "local" });
      if (error) throw error;
    }
  }), [loading, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider.");
  return value;
}
